import React, { useEffect, useRef, useState } from 'react';
import { Button, Flex, Input, Tag, Typography } from 'antd';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ApiError, fetchExchangeCard, type ExchangeCard } from '../../shared/api/client';
import HintLabel from '../../shared/ui/HintLabel';

export interface ExchangeCardRef {
  xid: string;
  title?: string | null;
  supplier?: string | null;
  /** Фото карточки — для миниатюры строки в истории расчётов (Keepa карточку биржи не знает). */
  image?: string | null;
}

interface ExchangeCardSourceProps {
  value: ExchangeCardRef | null;
  onLoaded: (card: ExchangeCard) => void;
  /** Номер, с которым платформа открыла модуль из карточки биржи: загрузить сразу. */
  autoLoadXid?: string | null;
}

const XID_RE = /^\d{1,12}$/;

/**
 * Расчёт с биржи поставщиков (WS 15587410, п.6): вместо ASIN — номер карточки биржи.
 * С карточки — себестоимость, размеры упаковки и вес; цену продажи и комиссии человек
 * вводит сам: в Keepa по карточке биржи данных нет.
 */
const ExchangeCardSource: React.FC<ExchangeCardSourceProps> = ({ value, onLoaded, autoLoadXid }) => {
  const { t } = useTranslation();
  const [xid, setXid] = useState(value?.xid ?? autoLoadXid ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<ExchangeCard | null>(null);
  const autoLoaded = useRef(false);

  const load = async (number: string) => {
    if (!XID_RE.test(number)) {
      setError(t('idea_calc.exchange.error_number'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const loaded = await fetchExchangeCard(number);
      setCard(loaded);
      onLoaded(loaded);
    } catch (e: unknown) {
      const code = e instanceof ApiError ? e.status : undefined;
      setError(
        t(
          code === 404
            ? 'idea_calc.exchange.error_not_found'
            : code === 401 || code === 428
              ? 'idea_calc.exchange.error_platform_only'
              : 'idea_calc.exchange.error_unavailable',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoadXid && !autoLoaded.current && XID_RE.test(autoLoadXid)) {
      autoLoaded.current = true;
      void load(autoLoadXid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadXid]);

  const shown = card ?? (value ? { xid: value.xid, title: value.title ?? null, supplier: value.supplier ?? null } : null);
  const box = card?.box;

  return (
    <div data-testid="exchange-card-source">
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <Input
          size="small"
          style={{ flex: 1 }}
          value={xid}
          inputMode="numeric"
          placeholder={t('idea_calc.exchange.placeholder')}
          status={error ? 'error' : undefined}
          onChange={(e) => {
            setXid(e.target.value.replace(/\D/g, '').slice(0, 12));
            setError(null);
          }}
          onPressEnter={() => void load(xid)}
        />
        <Button size="small" type="primary" icon={<CloudDownloadOutlined />} loading={loading} disabled={!xid} onClick={() => void load(xid)}>
          {t('idea_calc.exchange.load_button')}
        </Button>
      </div>
      {error && <Typography.Text type="danger" style={{ fontSize: 12 }}>{error}</Typography.Text>}
      {shown && (
        <Flex gap="small" wrap align="center" style={{ marginTop: 4 }}>
          <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>{t('idea_calc.exchange.card_tag', { xid: shown.xid })}</Tag>
          {shown.title && <Typography.Text style={{ fontSize: 13 }}>{shown.title}</Typography.Text>}
          {shown.supplier && <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {shown.supplier}</Typography.Text>}
          {card?.min_lot != null && <Tag style={{ marginInlineEnd: 0 }}>MOQ {card.min_lot}</Tag>}
          {box?.amount != null && (
            <Tag style={{ marginInlineEnd: 0 }}>
              {t('idea_calc.exchange.box', { amount: box.amount, kg: box.gross_kg ?? '—' })}
            </Tag>
          )}
        </Flex>
      )}
      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
        <HintLabel hint={t('idea_calc.exchange.tips.manual')}>{t('idea_calc.exchange.manual_hint')}</HintLabel>
      </Typography.Text>
    </div>
  );
};

export default ExchangeCardSource;
